import {
  Binary,
  BarChart3,
  Sliders,
  TrendingUp,
  Crosshair,
  Gauge,
  ShieldCheck,
  Monitor,
  CloudOff,
  Cpu,
  ShieldOff,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

// ---------------------------------------------------------------------------
// Module cards
// ---------------------------------------------------------------------------

export interface ModuleInfo {
  to: string
  title: string
  description: string
  icon: LucideIcon
  tests: string
  status: 'Standard' | 'Novel' | 'Integrated'
  testList: string[]
}

export const modules: ModuleInfo[] = [
  {
    to: '/sp800-22',
    title: 'NIST SP 800-22',
    description:
      'The established standard for RNG evaluation. Run the full 15-test statistical suite on any binary bitstream to assess randomness quality.',
    icon: Binary,
    tests: '15 tests',
    status: 'Standard',
    testList: [
      'Frequency (Monobit)',
      'Block Frequency',
      'Runs',
      'Longest Run of Ones',
      'Binary Matrix Rank',
      'Discrete Fourier Transform',
      'Non-Overlapping Template Matching',
      'Overlapping Template Matching',
      'Universal Statistical (Maurer)',
      'Linear Complexity',
      'Serial',
      'Approximate Entropy',
      'Cumulative Sums',
      'Random Excursions',
      'Random Excursions Variant',
    ],
  },
  {
    to: '/sp800-90b',
    title: 'NIST SP 800-90B',
    description:
      'Entropy source evaluation for cryptographic applications. Tests for IID hypothesis and computes min-entropy using 10 independent estimators.',
    icon: BarChart3,
    tests: '10 estimators',
    status: 'Standard',
    testList: [
      'IID Hypothesis Tests (permutation + chi-squared)',
      'Most Common Value (MCV)',
      'Collision Estimator',
      'Markov Estimator',
      'Compression Estimator',
      't-Tuple Estimator',
      'LZ78Y Prediction Estimator',
      'Multi Most Common in Window (MultiMCW)',
      'Lag Prediction Estimator',
      'Multi Markov Model with Counting (MultiMMC)',
      'Longest Repeated Substring (LRS)',
    ],
  },
  {
    to: '/tunable-bias',
    title: 'Tunable-Bias Analysis',
    description:
      'A novel framework for testing non-uniform random sources. Validates H\u2080: P(1) = p\u2080 for arbitrary bias, essential for p-bit device characterization.',
    icon: Sliders,
    tests: '7 tests',
    status: 'Novel',
    testList: [
      'Bias Accuracy Test',
      'Bias Stability (Allan Variance)',
      'Generalized Runs Test',
      'Generalized Serial Test',
      'Generalized Approximate Entropy',
      'Autocorrelation Analysis',
      'Conditional Entropy Ladder',
    ],
  },
  {
    to: '/gaussian',
    title: 'Gaussian Distribution',
    description:
      'Distribution validation for continuous-valued RNGs. Includes the PIT bridge for transforming Gaussian samples into binary bitstreams for further testing.',
    icon: TrendingUp,
    tests: '4 tests + Q-Q',
    status: 'Integrated',
    testList: [
      'Anderson-Darling Test',
      'Kolmogorov-Smirnov Test',
      'Shapiro-Wilk Test',
      'Jarque-Bera Test',
      'Q-Q Plot Visualization',
    ],
  },
]

// ---------------------------------------------------------------------------
// Stats bar
// ---------------------------------------------------------------------------

export interface StatItem {
  value: number
  label: string
  suffix?: string
}

// Update this floor when test count crosses next hundred
export const stats: StatItem[] = [
  { value: 15, label: 'SP 800-22 Statistical Tests' },
  { value: 10, label: 'SP 800-90B Entropy Estimators' },
  { value: 7, label: 'Tunable-Bias Tests' },
  { value: 800, label: 'Unit Tests Passing', suffix: '+' },
]

// ---------------------------------------------------------------------------
// Decision guide
// ---------------------------------------------------------------------------

export interface DecisionGuideItem {
  dataType: string
  dataDetail: string
  suite: string
  to: string
  icon: LucideIcon
}

export const decisionGuide: DecisionGuideItem[] = [
  {
    dataType: 'Binary bitstream',
    dataDetail: 'Uniform, p \u2248 0.5',
    suite: 'SP 800-22 \u2014 Full 15-test NIST suite',
    to: '/sp800-22',
    icon: Binary,
  },
  {
    dataType: 'Raw entropy source',
    dataDetail: 'Byte-level output from hardware',
    suite: 'SP 800-90B \u2014 IID testing + min-entropy',
    to: '/sp800-90b',
    icon: BarChart3,
  },
  {
    dataType: 'Biased binary source',
    dataDetail: 'p \u2260 0.5, e.g. MTJ p-bits',
    suite: 'Tunable-Bias \u2014 Bernoulli(p) validation',
    to: '/tunable-bias',
    icon: Sliders,
  },
  {
    dataType: 'Continuous values',
    dataDetail: 'Gaussian measurements',
    suite: 'Gaussian \u2014 Distribution tests + PIT bridge',
    to: '/gaussian',
    icon: TrendingUp,
  },
]

// ---------------------------------------------------------------------------
// Scientific rigor pillars
// ---------------------------------------------------------------------------

export interface RigorPillar {
  icon: LucideIcon
  title: string
  points: string[]
}

export const rigorPillars: RigorPillar[] = [
  {
    icon: Crosshair,
    title: 'Mathematical Precision',
    points: [
      'Upper incomplete gamma (igamc) ported from Cephes with \u226512 significant digit precision \u2014 the foundation for 11 of 15 SP 800-22 p-values',
      'erfc computed via Abramowitz & Stegun 7.1.26 rational approximation',
      'FFT: Radix-2 Cooley-Tukey with proper zero-padding to power of 2',
    ],
  },
  {
    icon: Gauge,
    title: 'Type I Error Calibration',
    points: [
      '200 Monte Carlo trials per test at \u03b1 = 0.01',
      'False rejection rate verified within Binomial(200, 0.01) 99% confidence interval',
      'Calibrated for both uniform (p = 0.5) and biased (p \u2260 0.5) sources',
    ],
  },
  {
    icon: ShieldCheck,
    title: 'Reference Vector Validation',
    points: [
      'Every SP 800-22 test validated against exact hand-computed examples from NIST SP 800-22 Rev 1a (\u00A72.1.8\u20132.15.8)',
      'Golden-master tests with deterministic LCG + Xorshift128Plus seeds, frozen to 4+ significant digits',
      'Full cross-validation suite runs on every commit',
    ],
  },
]

// ---------------------------------------------------------------------------
// Cross-validation tables
// ---------------------------------------------------------------------------

export interface ValidationRow {
  id: string
  test: string
  input: string
  ourPValue: string
  nistRef: string
  digits: number
}

export const sp80022Validations: ValidationRow[] = [
  {
    id: '1',
    test: 'Frequency',
    input: '\u03B5 = 1011010101, n = 10',
    ourPValue: '0.5271',
    nistRef: '0.5271',
    digits: 4,
  },
  {
    id: '2',
    test: 'Block Frequency',
    input: '\u03B5 = 0110011010, M = 3',
    ourPValue: '0.8013',
    nistRef: '0.8013',
    digits: 4,
  },
  {
    id: '3',
    test: 'Runs',
    input: '\u03B5 = 1001101011, n = 10',
    ourPValue: '0.1473',
    nistRef: '0.1473',
    digits: 3,
  },
  {
    id: '6',
    test: 'DFT / Spectral',
    input: '\u03B5 = 1001010011, n = 10',
    ourPValue: '0.5164',
    nistRef: '0.5164',
    digits: 4,
  },
  {
    id: '13',
    test: 'Cumulative Sums',
    input: '\u03B5 = 1011010111, n = 10',
    ourPValue: '0.4116',
    nistRef: '0.4116',
    digits: 4,
  },
  {
    id: "1'",
    test: 'Frequency (binary \u03C0)',
    input: '100-bit binary \u03C0 prefix',
    ourPValue: '0.1615',
    nistRef: '0.1615',
    digits: 4,
  },
]

export interface GoldenMasterRow {
  id: string
  test: string
  method: string
  detail: string
}

export const goldenMasterValidations: GoldenMasterRow[] = [
  { id: '4', test: 'Longest Run', method: 'Golden-master LCG (1M bits)', detail: '6-digit match' },
  { id: '5', test: 'Binary Matrix Rank', method: 'Deterministic pattern', detail: '\u03C7\u00B2 = 629.048' },
  { id: '7', test: 'Non-Overlapping Template', method: 'Golden-master LCG (1M bits)', detail: '4+ digit match' },
  { id: '8', test: 'Overlapping Template', method: 'Hamano & Kaneko (2007) corrected', detail: '4+ digit match' },
  { id: '9', test: 'Universal (Maurer)', method: 'Golden-master LCG (400K bits)', detail: 'Stat = 584.132' },
  { id: '10', test: 'Linear Complexity', method: 'Golden-master Xorshift (1M bits)', detail: '4+ digit match' },
  { id: '11', test: 'Serial', method: 'Golden-master LCG (10K bits)', detail: 'Stat = 29038.51' },
  { id: '12', test: 'Approximate Entropy', method: 'Golden-master LCG (seed 99)', detail: 'Stat = 10619.04' },
  { id: '14', test: 'Random Excursions', method: 'Golden-master Xorshift (1M bits)', detail: '4+ digit match' },
  { id: '15', test: 'Random Excursions Variant', method: 'Golden-master Xorshift (1M bits)', detail: '4+ digit match' },
]

export interface SP90BEstimatorRow {
  name: string
  validation: string
}

export const sp90bEstimators: SP90BEstimatorRow[] = [
  { name: 'Most Common Value (MCV)', validation: 'Golden-master (Xorshift128+, 10K samples)' },
  { name: 'Collision', validation: 'Golden-master + theoretical bounds' },
  { name: 'Markov', validation: 'Golden-master + monotonicity vs. bias' },
  { name: 'Compression', validation: 'Golden-master (Xorshift128+)' },
  { name: 't-Tuple', validation: 'Golden-master + theoretical bounds' },
  { name: 'LZ78Y', validation: 'Frequency-based prediction per SP 800-90B \u00A76.3.10' },
  { name: 'Multi MCW', validation: 'Golden-master (Xorshift128+)' },
  { name: 'Lag Prediction', validation: 'Golden-master + boundary checks' },
  { name: 'Multi MMC', validation: 'Golden-master (Xorshift128+)' },
  { name: 'LRS', validation: 'Golden-master + theoretical bounds' },
]

// ---------------------------------------------------------------------------
// Architecture columns
// ---------------------------------------------------------------------------

export interface ArchColumn {
  icon: LucideIcon
  secondaryIcon?: LucideIcon
  title: string
  description: string
}

export const archColumns: ArchColumn[] = [
  {
    icon: Monitor,
    secondaryIcon: CloudOff,
    title: 'Client-Side Only',
    description:
      'All computation runs entirely in your browser. No server, no API calls, no data upload \u2014 ever.',
  },
  {
    icon: Cpu,
    title: 'Web Worker Pipeline',
    description:
      'Heavy computation is offloaded to Web Workers so the UI stays responsive. Peak memory < 200 MB.',
  },
  {
    icon: ShieldOff,
    title: 'Zero Telemetry',
    description:
      'No analytics, no tracking, no cookies. Your experimental data stays on your machine.',
  },
]

// ---------------------------------------------------------------------------
// Novel contributions
// ---------------------------------------------------------------------------

export interface NovelContribution {
  id: string
  title: string
  description: string
  module: string
}

export const novelContributions: NovelContribution[] = [
  {
    id: 'N1',
    title: 'Generalized Runs Test',
    description: 'Extends classical runs test to arbitrary Bernoulli(p) bias',
    module: 'Tunable-Bias',
  },
  {
    id: 'N2',
    title: 'Generalized Serial Test',
    description: 'Serial test adapted for non-uniform binary sources',
    module: 'Tunable-Bias',
  },
  {
    id: 'N3',
    title: 'Generalized Approximate Entropy',
    description: 'ApEn for biased sources with corrected expected values',
    module: 'Tunable-Bias',
  },
  {
    id: 'N4',
    title: 'Allan Variance for Binary RNG',
    description: 'Bias stability over time using Allan variance methodology',
    module: 'Tunable-Bias',
  },
  {
    id: 'N5',
    title: 'Conditional Entropy Ladder',
    description: 'Visualization of conditional entropy at increasing block depths',
    module: 'Tunable-Bias',
  },
  {
    id: 'N6',
    title: 'Integrated PIT Pipeline',
    description: 'Gaussian \u2192 uniform \u2192 binary \u2192 SP 800-22 bridge',
    module: 'Gaussian',
  },
  {
    id: 'N7',
    title: 'Bias Accuracy Test',
    description: 'Confidence interval validation for Bernoulli(p\u2080) hypothesis',
    module: 'Tunable-Bias',
  },
  {
    id: 'N8',
    title: 'Complete Browser-Based SP 800-90B',
    description: 'First full client-side implementation of all 10 estimators',
    module: 'SP 800-90B',
  },
]
